import { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null); // null = checking, false = anon, object = admin
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/auth/me");
                setUser(data);
            } catch (err) {
                // Stale/expired token in localStorage → clear it so login flow is clean
                if (err?.response?.status === 401) {
                    try { localStorage.removeItem("itzkatu_token"); } catch {}
                }
                setUser(false);
            }
        })();
    }, []);

    const login = async (email, password) => {
        setError("");
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data?.access_token) localStorage.setItem("itzkatu_token", data.access_token);
            setUser(data);
            return true;
        } catch (e) {
            // Give more specific errors based on what actually failed
            let msg;
            if (e?.code === "ERR_NETWORK" || (!e?.response && e?.message?.includes("Network"))) {
                msg = "network error — backend unreachable from your browser. try again or check your connection.";
            } else if (e?.response?.status === 401) {
                msg = "invalid email or password.";
            } else if (e?.response?.data?.detail) {
                msg = formatApiError(e.response.data.detail);
            } else {
                msg = `error ${e?.response?.status || ""}: ${e?.message || "unknown"}`;
            }
            setError(msg);
            return false;
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {}
        localStorage.removeItem("itzkatu_token");
        setUser(false);
    };

    return <AuthContext.Provider value={{ user, error, login, logout, setUser }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
